import { ExpandableCard } from '@lmig/lmds-react-card';
import { Heading } from '@lmig/lmds-react-typography';
import { SimpleTable } from 'Foundation/Common/SimpleTable';
import { useUserData } from 'Foundation/User/UserContext';
import { useEffect, useState } from 'react';
import {
  GetAgencyCoupons,
  GetAgencyRewards,
  GetMarketingInitiatives,
  GetSecurityGroups,
  GetUserGroups,
  GetAgencies,
  GetProducerPerformance,
  GetApplicationAccess,
} from 'Foundation/User/UserApi';
import { IAgencyCouponData, IAgencyRewardData, IMarketingInitiative } from 'Foundation/User/UserContextModels';
import { GetUserSessionObjects } from './Helpers/AdminApi';
import { GetAgentStatCodes } from 'Foundation/StatCode/StatCodeHelper';
import { IAgentStatCode } from 'Foundation/StatCode/StatModels';
import { UserInfoAgencyMetrics } from './InfoCharts/UserInfoAgencyMetrics';
import { UserInfoQuoteData } from './InfoCharts/UserInfoQuoteData';
import { AgencyInfo } from './AdminTiles/AgencyInfo';
import { useUserCampaigns } from 'Foundation/User/UserCampaignsContext';
import './UserInfo.scss'

export function UserInfo() {
  const { userData } = useUserData();
  const { userCampaigns, getUserCampaigns } = useUserCampaigns();
  const [agencies, setAgencies] = useState<any[]>([]);
  const [applicationAccess, setApplicationAccess] = useState<string[]>([]);
  const [securityGroups, setSecurityGroups] = useState<string[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [marketingInitiatives, setMarketingInitiatives] = useState<IMarketingInitiative[]>([]);
  const [agencyRewards, setAgencyRewards] = useState<IAgencyRewardData[]>([]);
  const [agencyCoupons, setAgencyCoupons] = useState<IAgencyCouponData[]>([]);
  const [producerPerformance, setProducerPerformance] = useState<IProducerPerformance>();
  const [sessionCache, setSessionCache] = useState<string[][]>([]);
  const [subAgencies, setSubAgencies] = useState<IAgentStatCode[]>([]);
  const campaigns = userCampaigns ?? [];
  
  useEffect(() => {
    async function getAgencies() {
      let agentAgencies = await GetAgencies();
      setAgencies(agentAgencies);
    }
    async function getApplicationAccess() {
      let agentApplicationAccess = await GetApplicationAccess();
      setApplicationAccess(agentApplicationAccess);
    }
    async function getSecurityGroups() {
      let agentSecurityGroups = await GetSecurityGroups();
      setSecurityGroups(agentSecurityGroups);
    }
    async function getUserGroups() {
      let agentGroups = await GetUserGroups();
      setUserGroups(agentGroups);
    }
    async function getMarketingInitiatives() {
      let agentMarketingInitiatives = await GetMarketingInitiatives();
      setMarketingInitiatives(agentMarketingInitiatives);
    }
    async function getAgencyRewards() {
      let agentAgencyRewards = await GetAgencyRewards();
      setAgencyRewards(agentAgencyRewards);
    }
    async function getAgencyCoupons() {
      let agentAgencyCoupons = await GetAgencyCoupons();
      setAgencyCoupons(agentAgencyCoupons.filter((coupon) => coupon.Code !== null));
    }
    async function getSessionCacheObject() {
      let cache = await GetUserSessionObjects();
      setSessionCache(cache);
    }
    async function getAgentStatCodes() {
      let subStats = await GetAgentStatCodes();
      setSubAgencies(subStats);
    }
    async function getProducerPerformance() {
      let perf = await GetProducerPerformance();
      setProducerPerformance(perf);
    }
    getAgencies();
    if (userData?.isBL())
      getApplicationAccess();
    getSecurityGroups();
    getUserGroups();
    getMarketingInitiatives();
    getUserCampaigns(false);
    getAgencyRewards();
    getProducerPerformance();
    getAgencyCoupons();
    getSessionCacheObject();
    if (userData?.isPL())
      getAgentStatCodes();
  }, []);

  return (
    <>
      {userData && (
        <div className="user-info">
          <div>
            <div className="admin-tools-row">
              
              <div className="col-sm-12 col-md-6">
                <AgencyInfo />
              </div>

              <div className="col-sm-12 col-md-6">
                {userData.isBL() && (
                  <ExpandableCard
                  defaultExpanded={false}
                  className={`${applicationAccess && applicationAccess.length == 0 ? 'disabled-card' : ''}`}
                    cardTitle={
                      <Heading type="h4-light">
                        Products - {userData.selectedStatCodeData.SubStat}{' '}
                        {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>}
                      </Heading>
                    }
                  >
                    <SimpleTable tableObject={applicationAccess} nameProp="name" valueProp="value" />
                  </ExpandableCard>
              )}

                <ExpandableCard
                  defaultExpanded={false}
                  className={`${marketingInitiatives && marketingInitiatives.length == 0 ? 'disabled-card' : ''}` }
                  cardTitle={
                    <Heading type="h4-light">
                      Marketing Initiatives{' '}
                      {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>} (
                      {marketingInitiatives.length})
                    </Heading>
                  }
                >
                  <div >
                    {marketingInitiatives.map((initiative, index) => (
                      <div key={index}>
                        <pre>{JSON.stringify(initiative, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                </ExpandableCard>
                <ExpandableCard
                  defaultExpanded={false}
                  className={`${campaigns.length == 0 ? 'disabled-card' : ''}`}
                  cardTitle={
                    <Heading type="h4-light">
                      Campaigns{' '}
                      {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>} (
                      {campaigns.length})
                    </Heading>
                  }
                >
                  <div>
                    {campaigns.map((campaign, index) => (
                      <div key={index}>
                        <pre>{JSON.stringify(campaign, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                </ExpandableCard>
                <ExpandableCard
                defaultExpanded={false}
                
                className={`${agencyRewards && agencyRewards.length == 0 ? 'disabled-card' : ''}`}
                  cardTitle={
                    <Heading type="h4-light">
                      Agency Rewards {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>}{' '}
                      ({agencyRewards.length})
                    </Heading>
                  }
                >
                  <div className="container">
                    {agencyRewards.map((reward, index) => (
                      <div className="row" key={index}>
                        <pre>{JSON.stringify(reward, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                </ExpandableCard>
                <ExpandableCard
                defaultExpanded={false}
                className={`${!producerPerformance ? 'disabled-card' : ''}`}
                  cardTitle={
                    <Heading type="h4-light">
                      Producer Perfomance{' '}
                      {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>}
                    </Heading>
                  }
                >
                  <div className="container">
                    <div className="row">
                      <pre>{JSON.stringify(producerPerformance, null, 2)}</pre>
                    </div>
                  </div>
                </ExpandableCard>
                <ExpandableCard
                defaultExpanded={false}
                className={`${agencyCoupons && agencyCoupons.length == 0 ? 'disabled-card' : ''}`}
                  cardTitle={
                    <Heading type="h4-light">
                      Agency Coupons {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>}{' '}
                      ({agencyCoupons.length})
                    </Heading>
                  }
                >
                  <div className="container">
                    {agencyCoupons.map((coupon, index) => (
                      <div className="row" key={index}>
                        <pre>{JSON.stringify(coupon, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                </ExpandableCard>
                <ExpandableCard
                defaultExpanded={false}
                className={`${agencies && agencies.length == 0 ? 'disabled-card' : ''}`}
                  cardTitle={
                    <Heading type="h4-light">
                      Other Agencies {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>}{' '}
                      ({agencies.length})
                    </Heading>
                  }
                >
                  <div className="container">
                    {agencies.map((agency, index) => (
                      <div className="row" key={index}>
                        <pre>{JSON.stringify(agency, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                </ExpandableCard>
                {userData.isPL() && <ExpandableCard
                    defaultExpanded={false}
                    className={`${subAgencies && subAgencies.length == 0 ? 'disabled-card' : ''}`}
                    cardTitle={
                      <Heading type="h4-light">
                        User Stat Codes{' '}
                        {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.dpecid})</span>} (
                        {subAgencies.length})
                      </Heading>
                    }
                  >
                    <div className="container">
                      {subAgencies.map((agency, index) => (
                        <div className="row" key={index}>
                          <pre>{JSON.stringify(agency, null, 2)}</pre>
                        </div>
                      ))}
                    </div>
                  </ExpandableCard>}
                <ExpandableCard
                defaultExpanded={false}
                className={`${securityGroups && securityGroups.length == 0 ? 'disabled-card' : ''}`}
                  cardTitle={
                    <Heading type="h4-light">
                      Security Groups{' '}
                      {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.userId})</span>} (
                      {securityGroups.length})
                    </Heading>
                  }
                >
                  <SimpleTable tableObject={securityGroups} nameProp="" valueProp="" />
                </ExpandableCard>
                {userData.isBL() && <ExpandableCard
                defaultExpanded={false} className={`${userGroups.length == 0 ? 'disabled-card' : ''}`}
                    cardTitle={
                      <Heading type="h4-light">
                        User Groups
                        {!userData.isAgent() && (
                          <span style={{ color: 'blue' }}>
                            ({!userData.isMockingAgent ? userData.userId : userData.dpecid})
                          </span>
                        )}{' '}
                        ({userGroups.length})
                      </Heading>
                    }
                  >
                    <SimpleTable tableObject={userGroups} nameProp="" valueProp="" />
                  </ExpandableCard>}
                  <ExpandableCard
                defaultExpanded={false}
                className={`${sessionCache && sessionCache.length == 0 ? 'disabled-card' : ''}`}
                  cardTitle={
                    <Heading type="h4-light">
                      USCM Session Caches{' '}
                      {!userData.isAgent() && <span style={{ color: 'blue' }}>({userData.userId})</span>} (
                      {sessionCache.length})
                    </Heading>
                  }
                >
                  <div className="container">
                    <SimpleTable tableObject={sessionCache} nameProp="0" valueProp="1" />
                  </div>
                </ExpandableCard>
              </div>
              </div>

              {userData.isPL() && (
                <div className="admin-tools-row">
                  <div className="col-sm-12 col-md-6">
                    <UserInfoQuoteData />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <UserInfoAgencyMetrics />
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </>
  );
}